import type { ReactNode } from 'react';
import { ChatBubbleLeftEllipsisIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CountPill } from '../CountTabLabel';
import { NoteChip } from '../design-dx/HandBackBanner';
import { MAX_HAND_BACK_NOTE_LENGTH, noteSectionLabel } from '../design-dx/handBackNotes';
import { CARD_LABEL_CLASS, CARD_TEXT_CLASS, HEADER_BUTTON_CLASS } from '../design-dx/VersionCard';
import type { DraftNote } from './useDraftNotes';

interface NoteHandlers {
    onUpdate: (id: string, text: string) => void;
    onRemove: (id: string) => void;
}

interface AddNoteButtonProps {
    onClick: () => void;
    disabled: boolean;
}

export function AddNoteButton({ onClick, disabled }: AddNoteButtonProps) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} className={HEADER_BUTTON_CLASS}>
            + Note
        </button>
    );
}

function NotesEmptyState({ tabNames }: { tabNames: string }) {
    return (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-4 py-6 flex flex-col items-center gap-1.5 text-center">
            <ChatBubbleLeftEllipsisIcon className="w-6 h-6 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">No notes</span>
            <p className="text-xs text-gray-500 leading-relaxed">
                Use + Note on {tabNames} to flag what needs changing. Notes hand the case back; with
                none you can approve.
            </p>
        </div>
    );
}

interface NoteCardProps extends NoteHandlers {
    note: DraftNote;
    number: number;
}

function NoteCard({ note, number, onUpdate, onRemove }: NoteCardProps) {
    const length = note.text.trim().length;
    const overLimit = length > MAX_HAND_BACK_NOTE_LENGTH;
    return (
        <div className="flex-shrink-0 rounded-lg border border-gray-300 bg-white overflow-hidden transition-colors focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
                <span className={CARD_LABEL_CLASS}>Note {number}</span>
                {note.section && (
                    <NoteChip tone={note.section}>{noteSectionLabel(note.section)}</NoteChip>
                )}
                <button
                    type="button"
                    onClick={() => onRemove(note.id)}
                    aria-label={`Remove note ${number}`}
                    className="ml-auto flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    <XMarkIcon className="w-3.5 h-3.5" />
                </button>
            </div>
            {/* field-sizing grows the box with its text; rows is the fallback where it's unsupported. */}
            <textarea
                value={note.text}
                onChange={(e) => onUpdate(note.id, e.target.value)}
                placeholder="What should the clinical team change?"
                rows={3}
                autoFocus
                spellCheck
                className={`${CARD_TEXT_CLASS} block w-full px-3 py-2.5 [field-sizing:content] min-h-[5.5rem] max-h-[18.5rem] overflow-y-auto resize-none outline-none bg-transparent placeholder-gray-400`}
            />
            <div
                className={`px-3 pb-2 text-right text-[11px] tabular-nums ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
            >
                {length.toLocaleString()} / {MAX_HAND_BACK_NOTE_LENGTH.toLocaleString()}
            </div>
        </div>
    );
}

type WithNotesProps = NoteHandlers & {
    notes: DraftNote[];
    children: ReactNode;
    // The work tabs carrying + Note, named in the empty state.
    tabNames?: string;
};

// A work tab's content with the reviewer's notes column beside it.
export function WithNotes({
    notes,
    onUpdate,
    onRemove,
    children,
    tabNames = 'Submissions or Dx report'
}: WithNotesProps) {
    return (
        <div className="flex-1 min-h-0 flex font-inter">
            <div className="flex-1 min-w-0 flex flex-col">{children}</div>
            <aside
                aria-label="Notes for the clinical team"
                className="w-[280px] flex-shrink-0 min-h-0 flex flex-col border-l border-gray-200 bg-gray-50"
            >
                <div className="px-4 h-12 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
                    <span className="font-bold text-sm text-gray-800 truncate">
                        Notes for clinical team
                    </span>
                    {notes.length > 0 && <CountPill count={notes.length} tone="attention" />}
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide p-3.5 flex flex-col gap-3">
                    {notes.length === 0 ? (
                        <NotesEmptyState tabNames={tabNames} />
                    ) : (
                        notes.map((note, i) => (
                            <NoteCard
                                key={note.id}
                                note={note}
                                number={i + 1}
                                onUpdate={onUpdate}
                                onRemove={onRemove}
                            />
                        ))
                    )}
                </div>
            </aside>
        </div>
    );
}
